import React from "react";
import { FileSearch, Eye, CheckCircle2, Plus } from "lucide-react";
import {
  getItemLabel,
  isSettled,
  orderStatusClass,
  orderStatusLabel,
} from "Common/constants/proformaStatus";
import { ltrValue } from "Common/constants/panelSpec";

/**
 * ستون‌های مشترک همه‌ی بخش‌هایی که یک رکورد در آن‌ها جابه‌جا می‌شود:
 *   پیش‌فاکتورها → سفارش‌های فروش → سفارش‌های در حال تولید → سفارش‌های تولید شده →
 *   سفارش‌های در انبار → سفارش‌های خارج شده از انبار → سفارش‌های بارگیری شده →
 *   سفارش‌های تحویل شده به مشتری
 *
 * چون رکورد بین این بخش‌ها منتقل می‌شود، ترتیب و عنوان ستون‌ها باید در همه‌ی آن‌ها یکی
 * باشد. تنها ستونی که از صفحه‌ای به صفحه‌ی دیگر فرق می‌کند «عملیات» است که هر صفحه
 * خودش بعد از این ستون‌ها اضافه می‌کند.
 */

const formatAmount = (value: string | number | undefined | null) => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === undefined || num === null || isNaN(num)) return "0";
  return num.toLocaleString("en-US");
};
// نمایش درصد واریزی: null/undefined → «—» ، صفر → «۰٪» ، بزرگ‌تر از ۱۰۰ یا منفی → همان‌طور
const formatDepositPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  const rounded = Math.round(Number(value));
  if (!Number.isFinite(rounded)) return "—";
  return `${rounded.toLocaleString("fa-IR")}٪`;
};
export interface OrderColumnsProps {
  /** نام و نام خانوادگی کارشناس فروش ردیف */
  getSalesExpertName: (row: any) => string;
  /** باز کردن مودال مشخصات/جزئیات سفارش */
  onViewSpecs: (row: any) => void;
  /** باز کردن مودال فهرست پرداخت‌ها */
  onViewPayments: (row: any) => void;
  /** باز کردن مودال ثبت پرداخت جدید */
  onRegisterPayment: (row: any) => void;
  /** آیا کاربر جاری اجازه‌ی ثبت پرداخت روی این ردیف را دارد */
  canRegisterPayment: (row: any) => boolean;
  /**
   * جایگزین محتوای سلول «تأیید مدیر فروش». صفحه‌ی پیش‌فاکتورها از این پارامتر برای
   * نشان‌دادن دکمه‌های تأیید/لغو تأیید استفاده می‌کند؛ بقیه‌ی صفحه‌ها فقط‌خواندنی‌اند.
   */
  renderSalesManagerApproval?: (row: any) => React.ReactNode;
  /**
   * نمایش ستون «نام مشتری». برای کارشناس تولید و حراست باید false باشد؛ سرور هم
   * customer_name را برای این نقش‌ها نمی‌فرستد.
   */
  showCustomerName?: boolean;
  /**
   * درج ستون مستقل «تاریخ» بلافاصله بعد از «کارشناس فروش». وقتی روشن باشد، تاریخ
   * از زیرِ سلول کارشناس فروش برداشته می‌شود تا دوبار نمایش داده نشود.
   */
  showDateColumn?: boolean;
  /**
   * محتوای سلول «تأیید پورسانت». اگر داده شود، دو ستون «درصد پورسانت» و
   * «تأیید پورسانت» بلافاصله بعد از «نام مشتری» ساخته می‌شوند. صفحه‌ی فراخوان
   * تصمیم می‌گیرد دکمه باشد یا متن فقط‌خواندنی.
   */
  renderCommissionApproval?: (row: any) => React.ReactNode;
}

// دکمه‌های داخل جدول (مشخصات، ثبت پرداخت، نمایش پرداخت) باید در همه‌ی حالت‌ها
// شبیه دکمه دیده شوند، نه متن ساده‌ی ستون.
export const tableButtonClass =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 py-1.5 text-xs font-medium shadow-sm transition-colors";

// متن «شرح کالا» یک‌جا ساخته می‌شود تا سلول جدول و خروجی اکسل از هم جدا نیفتند.
const describeItems = (row: any): string => {
  const items = row?.items || [];
  if (items.length === 0) return "-";
  const [first, ...rest] = items;
  const firstLabel = getItemLabel(first, 0);
  return rest.length > 0 ? `${firstLabel} و ${rest.length} قلم دیگر` : firstLabel;
};

// مبالغ سرور رشته‌ی Decimal هستند؛ برای اکسل عدد برگردانده می‌شود تا قابل جمع‌زدن باشد.
const exportNumber = (value: any): number | string => {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  return isNaN(numeric) ? String(value) : numeric;
};

const EmptyCell = () => <span className="text-blue-800 text-xs">—</span>;

/**
 * متن درصد پورسانت. مقدار صفر معتبر است و نباید با خالی یکی شود، پس به‌جای
 * بررسی truthy، صریح با null/undefined/رشته‌ی خالی مقایسه می‌شود.
 */
/**
 * مبلغ پورسانت از پاسخ سرور خوانده می‌شود تا نمایش با تأیید و چاپ یکسان باشد.
 */
const commissionAmountOf = (row: any): number | null => {
  const raw = String(row?.commission_amount ?? "").trim();
  if (raw === "") return null;
  const amount = Number(raw);
  return Number.isFinite(amount) ? amount : null;
};

const commissionText = (row: any): string => {
  const value = row?.commission_percentage;
  if (value === null || value === undefined || String(value).trim() === "") return "";
  const numeric = Number(value);
  return Number.isFinite(numeric) ? String(numeric) : String(value);
};

export const getOrderColumns = ({
  getSalesExpertName,
  onViewSpecs,
  onViewPayments,
  onRegisterPayment,
  canRegisterPayment,
  renderSalesManagerApproval,
  showCustomerName = true,
  showDateColumn = false,
  renderCommissionApproval,
}: OrderColumnsProps) => ([
  // ===== ۱. کارشناس فروش =====
  {
    header: "کارشناس فروش",
    accessorKey: "sales_expert_fullname",
    exportValue: (row: any) => getSalesExpertName(row),
    cell: (cell: any) => (
      <div>
        <div className="font-medium">{getSalesExpertName(cell.row.original)}</div>
        {/* وقتی ستون مستقل «تاریخ» فعال است، تاریخ اینجا تکرار نمی‌شود. */}
        {!showDateColumn && (
          <div className="text-xs text-blue-800 dark:text-zink-300">
            {cell.row.original.created_at_jalali}
          </div>
        )}
      </div>
    ),
  },

  // ===== ۱.۵ تاریخ (فقط صفحه‌های «همه سفارش‌ها» و «سایر سفارش‌ها») =====
  // طبق مستند دقیقاً بعد از «کارشناس فروش» می‌نشیند. با فیلتر خالی از فهرست حذف
  // می‌شود (پایین همین آرایه) تا ترتیب بقیه‌ی صفحه‌ها دست‌نخورده بماند.
  ...(showDateColumn
    ? [
      {
        // طبق مستند نسخه ۴، تاریخ از invoice_date خوانده می‌شود (تاریخ تجاری فاکتور،
        // شمسی با قالب YYYY/MM/DD) — نه created_at که لحظه‌ی ثبت رکورد است. فیلتر
        // بازه‌ی تاریخ هم روی همین فیلد اعمال می‌شود.
        header: "تاریخ",
        accessorKey: "invoice_date",
        exportValue: (row: any) => row?.invoice_date ?? "",
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            // قفل جهت تا «۱۴۰۵/۰۷/۰۱» در متن راست‌به‌چپ جابه‌جا نشود.
            <span className="whitespace-nowrap">{ltrValue(String(value))}</span>
          ) : (
            <EmptyCell />
          );
        },
      },
    ]
    : []),

  // ===== ۲. شماره فاکتور =====
  // طبق مستند باید دقیقاً دومین ستون، بلافاصله بعد از «کارشناس فروش» باشد.
  {
    header: "شماره فاکتور",
    accessorKey: "invoice_number",
    exportValue: (row: any) => row?.invoice_number ?? "",
    cell: (cell: any) => {
      const value = cell.getValue();
      return value === null || value === undefined || value === "" ? (
        <EmptyCell />
      ) : (
        <span className="font-medium">{String(value)}</span>
      );
    },
  },

  // ===== ۳. نام مشتری =====
  {
    header: "نام مشتری",
    accessorKey: "customer_name",
    // سفارش می‌تواند فقط رابط داشته باشد، پس نام مشتری ممکن است خالی باشد.
    cell: (cell: any) => cell.getValue() || "-",
  },

  // ===== ۳. وضعیت سفارش =====
  {
    header: "وضعیت سفارش",
    accessorKey: "order_status",
    exportValue: (row: any) => orderStatusLabel(row?.order_status),
    cell: (cell: any) => {
      const status = cell.getValue() || "proforma";
      return (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${orderStatusClass(status)}`}
        >
          {orderStatusLabel(status)}
        </span>
      );
    },
  },

  // ===== ۴. کل متراژ =====
  // پاسخ فهرست فیلد `area` تک‌قلمی ندارد؛ مجموع متراژ سفارش در `total_area` می‌آید.
  {
    header: "کل متراژ",
    accessorKey: "total_area",
    enableSorting: false,
    exportValue: (row: any) => exportNumber(row?.total_area),
    cell: (cell: any) => {
      const value = cell.getValue();
      return value ? parseFloat(value).toFixed(2) : "-";
    },
  },

  // ===== ۵. شرح کالا =====
  {
    header: "شرح کالا",
    enableColumnFilter: false,
    exportValue: (row: any) => describeItems(row),
    cell: (cell: any) => {
      const items = cell.row.original.items || [];
      if (items.length === 0) return "-";
      const [first, ...rest] = items;
      return (
        <div className="flex flex-col">
          <span>{getItemLabel(first, 0)}</span>
          {rest.length > 0 && (
            <span className="text-xs text-blue-800 dark:text-zink-300">
              و {rest.length} قلم دیگر
            </span>
          )}
        </div>
      );
    },
  },

  // ===== ۶. مشخصات =====
  {
    header: "مشخصات",
    enableColumnFilter: false,
    exportable: false,
    cell: (cell: any) => (
      <button
        type="button"
        onClick={() => onViewSpecs(cell.row.original)}
        className={`${tableButtonClass} border-blue-500 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20`}
        title="مشخصات"
      >
        <FileSearch className="size-4" />
        <span>مشخصات</span>
      </button>
    ),
  },

  // ===== ۷. ثبت پرداخت =====
  {
    header: "ثبت پرداخت",
    enableColumnFilter: false,
    exportable: false,
    cell: (cell: any) => {
      const row = cell.row.original;
      if (!canRegisterPayment(row)) return <EmptyCell />;
      return (
        <button
          type="button"
          onClick={() => onRegisterPayment(row)}
          className={`${tableButtonClass} border-custom-500 text-custom-600 hover:bg-custom-50 dark:text-custom-400 dark:hover:bg-custom-900/20`}
          title="ثبت پرداخت"
        >
          <Plus className="size-4" />
          <span>ثبت پرداخت</span>
        </button>
      );
    },
  },

  // ===== ۸. نمایش پرداخت =====
  {
    header: "نمایش پرداخت",
    enableColumnFilter: false,
    exportable: false,
    cell: (cell: any) => (
      <button
        type="button"
        onClick={() => onViewPayments(cell.row.original)}
        className={`${tableButtonClass} border-teal-500 text-teal-600 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-900/20`}
        title="نمایش پرداخت"
      >
        <Eye className="size-4" />
        <span>پرداخت‌ها</span>
      </button>
    ),
  },

  // ===== ۹. مبلغ واریزی =====
  {
    header: "مبلغ واریزی",
    accessorKey: "deposited_amount",
    exportValue: (row: any) => exportNumber(row?.deposited_amount),
    cell: (cell: any) => formatAmount(cell.getValue()),
  },
  // ===== ۹. مبلغ واریزی =====


  // ===== ۱۰. درصد واریزی =====
  // مقدار مستقیماً از بک‌اند خوانده می‌شود؛ محاسبهٔ مجدد در فرانت‌اند انجام نمی‌شود.
  // نتیجه می‌تواند بزرگ‌تر از ۱۰۰ یا منفی باشد و به ۱۰۰ محدود نمی‌شود.
  {
    header: "درصد واریزی",
    accessorKey: "deposited_percentage",
    enableColumnFilter: false,
    meta: { serverSortBy: "deposited_percentage" },
    exportValue: (row: any) =>
      row?.deposited_percentage === null || row?.deposited_percentage === undefined
        ? ""
        : Math.round(Number(row.deposited_percentage)),
    cell: (cell: any) =>
      formatDepositPercentage(cell.row.original.deposited_percentage),
  },

  // ===== ۱۱. مبلغ واریزی تأییدشده =====
  {
    header: "مبلغ واریزی تأییدشده",
    accessorKey: "confirmed_deposited_amount",
    exportValue: (row: any) => exportNumber(row?.confirmed_deposited_amount),
    cell: (cell: any) => formatAmount(cell.getValue()),
  },

 

  // ===== ۱۱. تأیید مدیر فروش =====
  {
    header: "تأیید مدیر فروش",
    enableColumnFilter: false,
    exportValue: (row: any) => (row?.sales_manager_approved ? "تأیید شده" : "-"),
    cell: (cell: any) => {
      const row = cell.row.original;
      if (renderSalesManagerApproval) return renderSalesManagerApproval(row);
      return row.sales_manager_approved ? (
        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
          <CheckCircle2 className="size-4" />
          <span>تأیید شده</span>
        </span>
      ) : (
        <EmptyCell />
      );
    },
  },

  // ===== ۱۲. تعداد اقلام =====
  // وضعیت هر قلم (item_status) در پاسخ فهرست وجود ندارد و فقط در صفحه جزئیات
  // (ProformaInvoiceItemOut) در دسترس است، بنابراین اینجا تعداد اقلام نمایش داده می‌شود.
  {
    header: "تعداد اقلام",
    accessorKey: "item_count",
    cell: (cell: any) => cell.getValue() ?? 0,
  },

  // ===== ۱۳. مانده تا تسویه =====
  {
    header: "مانده تا تسویه",
    accessorKey: "remaining_balance",
    exportValue: (row: any) => exportNumber(row?.remaining_balance),
    cell: (cell: any) => formatAmount(cell.getValue()),
  },

  // ===== ۱۴. وضعیت تسویه =====
  // علاوه بر settlement_status سرور، وقتی «واریزی تأییدشده» به «مبلغ خالص» رسیده باشد
  // هم سبز و «تسویه شده» نمایش داده می‌شود (نگاه کنید به isSettled).
  {
    header: "وضعیت تسویه",
    accessorKey: "settlement_status",
    enableSorting: false,
    exportValue: (row: any) => (isSettled(row) ? "تسویه شده" : "تسویه نشده"),
    cell: (cell: any) => {
      const settled = isSettled(cell.row.original);
      return (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${settled
              ? "bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400"
              : "bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400"
            }`}
        >
          {settled ? "تسویه شده" : "تسویه نشده"}
        </span>
      );
    },
  },

  // ===== ۱۵. درصد پورسانت و تأیید مدیر فروشِ پورسانت =====
  // طبق خواسته، این دو ستون آخرین ستون‌های مشترک‌اند و بلافاصله پیش از ستون
  // «عملیات» می‌نشینند (هر صفحه «عملیات» را بعد از این‌ها اضافه می‌کند).
  // این تأیید کاملاً جدا از ستون «تأیید مدیر فروش» (تأیید خود سفارش) است.
  ...(renderCommissionApproval
    ? [
      {
        header: "درصد پورسانت",
        accessorKey: "commission_percentage",
        exportValue: (row: any) => commissionText(row),
        cell: (cell: any) => {
          const text = commissionText(cell.row.original);
          return text === "" ? <EmptyCell /> : <span className="font-medium">{text}٪</span>;
        },
      },
      {
        // مبلغ نهایی و رشته‌ی نمایشی را خود API برمی‌گرداند.
        header: "مبلغ پورسانت",
        accessorKey: "commission_amount",
        enableColumnFilter: false,
        meta: { serverSortBy: "commission_amount" },
        exportValue: (row: any) => {
          const amount = commissionAmountOf(row);
          return amount === null ? "" : amount;
        },
        cell: (cell: any) => {
          const amount = commissionAmountOf(cell.row.original);
          return amount === null ? (
            <EmptyCell />
          ) : (
            <span className="font-medium">
              {cell.row.original?.commission_amount_display || amount.toLocaleString("en-US")}
            </span>
          );
        },
      },
      {
        // عنوان ستون طبق عکس مستند «مدیر فروش» است؛ برچسب دکمه‌ی داخل سلول
        // «تأیید پورسانت» می‌ماند تا با ستون «تأیید مدیر فروش» اشتباه نشود.
        header: "مدیر فروش",
        enableColumnFilter: false,
        exportValue: (row: any) =>
          row?.commission_approved
            ? `تأیید شده${row?.commission_approved_at_jalali ? ` - ${row.commission_approved_at_jalali}` : ""}`
            : "",
        cell: (cell: any) => renderCommissionApproval(cell.row.original),
      },
    ]
    : []),
] as any[]).filter((column) => showCustomerName || column.header !== "نام مشتری");

export default getOrderColumns;
